import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import {
  fetchReceivedReviews,
  fetchGivenReviews,
  fetchPendingReviewTransactions,
  createTransactionReview,
} from '../../lib/reviewsService.js';
import WebTopBar from '../components/WebTopBar.js';
import SurcosRating from '../components/SurcosRating.js';
import Icon from '../components/Icon.js';
import { DCard, BtnGold, BtnGhost, Avatar } from '../components/MarketplaceAtoms.js';

function reviewerRow(r) {
  const rev = Array.isArray(r.reviewer) ? r.reviewer[0] : r.reviewer;
  return rev ?? {};
}

function reviewedRow(r) {
  const rev = Array.isArray(r.reviewed) ? r.reviewed[0] : r.reviewed;
  return rev ?? {};
}

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function shippingLabel(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('pickup')) return 'Recogida';
  if (t.includes('verified') || t.includes('verific')) return 'Partner verificador';
  if (t.includes('physical') || t.includes('meet')) return 'Entrega en mano';
  if (t.includes('direct') || t === '' || t.includes('ship')) return 'Envío directo';
  return type || 'Envío';
}

function listingCoverUrl(listing) {
  if (!listing) return null;
  const fromPhotos = Array.isArray(listing.photos) && listing.photos.length ? String(listing.photos[0]).trim() : null;
  return listing.cover_url ?? fromPhotos ?? null;
}

function listingSubtitle(listing, amount, shippingType) {
  if (!listing) {
    const parts = [];
    if (amount != null) parts.push(formatEuro(amount));
    if (shippingType) parts.push(shippingLabel(shippingType));
    return parts.length ? parts.join(' · ') : 'Transacción completada';
  }
  const artist = listing.artist ? `${listing.artist} · ` : '';
  const title = listing.title || 'Sin título';
  const cond = listing.condition ? ` · ${listing.condition}` : '';
  const price =
    amount != null
      ? ` · ${formatEuro(amount)}`
      : listing.price != null
        ? ` · ${formatEuro(listing.price)}`
        : '';
  return `${artist}${title}${cond}${price} · ${shippingLabel(shippingType)}`;
}

function counterpartyRoleLabel(role) {
  return role === 'buyer' ? 'Vendedor' : 'Comprador';
}

const ALL_TAGS = [
  'Descripción exacta',
  'Buen embalaje',
  'Envío rápido',
  'Buena comunicación',
  'Disco mejor de lo esperado',
  'Precio justo',
  'Muy amable',
  'Otro',
];

export default function PageValoraciones({ onBack, onNavigate, user, itemToRate }) {
  const reviewTxId = itemToRate?.reviewTransactionId ?? null;
  const [tab, setTab] = useState(reviewTxId ? 'valorar' : 'recibidas');
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [activeReview, setActiveReview] = useState(null);

  const [received, setReceived] = useState([]);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [given, setGiven] = useState([]);
  const [givenLoading, setGivenLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  const loadReceived = useCallback(async () => {
    if (!user?.id) {
      setReceived([]);
      return;
    }
    setReceivedLoading(true);
    const { data, error } = await fetchReceivedReviews(user.id, { onlyVisible: false, limit: 80 });
    if (!error) setReceived(Array.isArray(data) ? data : []);
    else setReceived([]);
    setReceivedLoading(false);
  }, [user?.id]);

  const loadGiven = useCallback(async () => {
    if (!user?.id) {
      setGiven([]);
      return;
    }
    setGivenLoading(true);
    const { data, error } = await fetchGivenReviews(user.id, { limit: 80 });
    if (!error) setGiven(Array.isArray(data) ? data : []);
    else setGiven([]);
    setGivenLoading(false);
  }, [user?.id]);

  const loadPending = useCallback(async () => {
    if (!user?.id) {
      setPending([]);
      return;
    }
    setPendingLoading(true);
    const { data, error } = await fetchPendingReviewTransactions(user.id);
    if (!error) setPending(Array.isArray(data) ? data : []);
    else setPending([]);
    setPendingLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (tab === 'recibidas') loadReceived();
  }, [tab, loadReceived]);

  useEffect(() => {
    if (tab === 'dadas') loadGiven();
  }, [tab, loadGiven]);

  useEffect(() => {
    if (tab === 'pendientes' || tab === 'valorar' || reviewTxId) loadPending();
  }, [tab, reviewTxId, loadPending]);

  useEffect(() => {
    if (!reviewTxId || !pending.length) return;
    const match = pending.find(p => p.transactionId === reviewTxId);
    if (match) setActiveReview(match);
  }, [reviewTxId, pending]);

  const recvStats = useMemo(() => {
    const rows = received.filter(r => r.is_visible);
    const n = rows.length;
    if (!n) return { avg: null, count: 0, surcos: 0 };
    const avg = rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / n;
    const rounded = Math.round(avg * 10) / 10;
    const surcos = Math.min(5, Math.max(1, Math.round(rounded)));
    return { avg: rounded, count: n, surcos };
  }, [received]);

  const givenStats = useMemo(() => {
    const rows = given.filter(r => r.is_visible);
    const n = rows.length;
    if (!n) return { avg: null, count: 0, surcos: 0 };
    const avg = rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / n;
    const rounded = Math.round(avg * 10) / 10;
    const surcos = Math.min(5, Math.max(1, Math.round(rounded)));
    return { avg: rounded, count: n, surcos };
  }, [given]);

  const submitReview = useCallback(async () => {
    const txId = activeReview?.transactionId;
    if (!txId) {
      setSubmitErr('Elige una transacción pendiente para valorar.');
      return;
    }
    if (!stars || stars < 1) {
      setSubmitErr('Elige una puntuación de 1 a 5 surcos.');
      return;
    }
    setSubmitBusy(true);
    setSubmitErr('');
    const { error } = await createTransactionReview({
      transactionId: txId,
      rating: stars,
      tags,
      comment: comment.trim() || null,
    });
    setSubmitBusy(false);
    if (error) {
      setSubmitErr(error);
      return;
    }
    setStars(0);
    setTags([]);
    setComment('');
    setActiveReview(null);
    setTab('dadas');
    loadGiven();
    loadPending();
  }, [activeReview, stars, tags, comment, loadGiven, loadPending]);

  const openPendingItem = useCallback(item => {
    setActiveReview(item);
    setStars(0);
    setTags([]);
    setComment('');
    setSubmitErr('');
    setTab('valorar');
  }, []);

  const pendingCount = pending.length;
  const pendingCountLabel =
    pendingCount === 0
      ? 'Sin valoraciones pendientes'
      : pendingCount === 1
        ? 'Tienes 1 valoración pendiente'
        : `Tienes ${pendingCount} valoraciones pendientes`;

  const valorarListing = activeReview?.listing ?? null;
  const valorarCover = listingCoverUrl(valorarListing);
  const valorarCounterparty = activeReview?.counterparty ?? {};
  const valorarUname = valorarCounterparty.username ?? 'usuario';

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar onBack={onBack} title="Valoraciones" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 0 60px' }}>
        <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.1)', padding: '0 20px' }}>
          {[
            ['valorar', 'Valorar'],
            ['recibidas', 'Como vendedor'],
            ['dadas', 'Como comprador'],
            ['pendientes', 'Pendientes'],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                padding: '14px 0',
                fontSize: 12,
                color: tab === k ? C.gold : 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
                marginRight: 16,
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${tab === k ? C.gold : 'transparent'}`,
                whiteSpace: 'nowrap',
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === 'valorar' && (
          <div style={{ padding: '20px' }}>
            {!user?.id ? (
              <DCard style={{ padding: 16 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Inicia sesión para valorar tus transacciones.</div>
              </DCard>
            ) : !activeReview ? (
              <>
                <DCard style={{ padding: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                    No hay ninguna transacción seleccionada. Elige una valoración pendiente para continuar.
                  </div>
                </DCard>
                <BtnGhost onClick={() => setTab('pendientes')}>Ver pendientes</BtnGhost>
              </>
            ) : (
              <>
                <DCard style={{ display: 'flex', gap: 12, padding: '12px 14px', marginBottom: 14, alignItems: 'center' }}>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 10,
                      background: '#2d1b69',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {valorarCover ? (
                      <img src={valorarCover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Icon name="disc" size={18} color="rgba(255,255,255,0.3)" />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>
                      {valorarListing?.title || valorarListing?.artist || 'Transacción'}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                      {listingSubtitle(valorarListing, activeReview.amount, activeReview.shippingType)}
                    </div>
                  </div>
                </DCard>

                <DCard style={{ display: 'flex', gap: 12, padding: '12px 14px', marginBottom: 14, alignItems: 'center' }}>
                  <Avatar
                    initials={valorarUname.slice(0, 2).toUpperCase()}
                    bg="#534AB7"
                    color="#EEEDFE"
                    size={40}
                    imageUrl={valorarCounterparty.avatar_url ?? null}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>@{valorarUname}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                      {counterpartyRoleLabel(activeReview.role)}
                    </div>
                  </div>
                </DCard>

                <DCard style={{ padding: '10px 14px', marginBottom: 16, background: 'rgba(83,74,183,0.08)', borderColor: 'rgba(83,74,183,0.2)' }}>
                  <p style={{ fontSize: 11, color: C.purpleL, lineHeight: 1.5 }}>
                    Tu valoración es confidencial hasta que la otra parte también valore la transacción, o hasta dentro de 7 días. Así ambos podéis ser completamente honestos.
                  </p>
                </DCard>

                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 500,
                      marginBottom: 10,
                    }}
                  >
                    Puntuación general
                  </div>
                  <SurcosRating value={stars} onChange={setStars} readOnly={false} size="lg" showLabel />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 500,
                      marginBottom: 10,
                    }}
                  >
                    ¿Qué destacarías?
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {ALL_TAGS.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTags(p => (p.includes(t) ? p.filter(x => x !== t) : [...p, t]))}
                        style={{
                          padding: '6px 13px',
                          borderRadius: 20,
                          fontSize: 11,
                          cursor: 'pointer',
                          border: `0.5px solid ${tags.includes(t) ? C.gold : 'rgba(255,255,255,0.15)'}`,
                          color: tags.includes(t) ? C.gold : 'rgba(255,255,255,0.55)',
                          background: tags.includes(t) ? 'rgba(200,168,75,0.12)' : 'transparent',
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 500,
                      marginBottom: 8,
                    }}
                  >
                    Comentario opcional
                  </div>
                  <textarea
                    rows={3}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder={`Cuéntanos tu experiencia con este ${activeReview.role === 'buyer' ? 'vendedor' : 'comprador'}...`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '0.5px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                    onFocus={e => (e.target.style.borderColor = C.gold)}
                    onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                  />
                </div>

                {submitErr ? (
                  <div style={{ fontSize: 11, color: C.warn, marginBottom: 10 }}>{submitErr}</div>
                ) : null}

                <BtnGold onClick={submitReview} disabled={submitBusy || !stars}>
                  {submitBusy ? 'Enviando…' : 'Enviar valoración'}
                </BtnGold>
                <BtnGhost style={{ marginTop: 8 }} onClick={() => setTab('pendientes')}>
                  Ahora no — valorar más tarde
                </BtnGhost>
              </>
            )}
          </div>
        )}

        {tab === 'recibidas' && (
          <div style={{ padding: '20px' }}>
            {!user?.id ? (
              <DCard style={{ padding: 16 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Inicia sesión para ver tus valoraciones recibidas.</div>
              </DCard>
            ) : receivedLoading ? (
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando…</div>
            ) : (
              <>
                <DCard style={{ display: 'flex', gap: 16, padding: 16, marginBottom: 14, alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: 34, fontWeight: 500, color: C.gold, lineHeight: 1 }}>
                      {recvStats.count === 0 ? '—' : recvStats.avg}
                    </div>
                    <SurcosRating value={recvStats.surcos} readOnly size="md" count={recvStats.count} />
                  </div>
                  <div style={{ flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
                    Media solo sobre valoraciones <strong style={{ color: 'rgba(255,255,255,0.75)' }}>visibles</strong>. Para responder con texto público usa el{' '}
                    <strong style={{ color: C.gold }}>Panel Partner → Valoraciones</strong>.
                  </div>
                </DCard>

                {!received.length ? (
                  <DCard style={{ padding: 16 }}>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Sin valoraciones recibidas todavía.</div>
                  </DCard>
                ) : (
                  received.map(r => {
                    const rev = reviewerRow(r);
                    const uname = rev.username ?? 'Usuario';
                    const sur = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 1)));
                    const tgs = Array.isArray(r.tags) ? r.tags : [];
                    return (
                      <DCard key={r.id} style={{ padding: 14, marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <Avatar initials={uname.slice(0, 2).toUpperCase()} bg="#534AB7" color="#EEEDFE" size={32} imageUrl={rev.avatar_url ?? null} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>@{uname}</div>
                            <SurcosRating value={sur} readOnly size="sm" />
                          </div>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : ''}
                          </span>
                        </div>
                        {!r.is_visible ? (
                          <div style={{ fontSize: 10, color: 'rgba(239,159,39,0.95)', marginBottom: 6 }}>No visible en público hasta moderación</div>
                        ) : null}
                        {r.comment?.trim() ? (
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 8 }}>{r.comment.trim()}</div>
                        ) : null}
                        {tgs.length ? (
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {tgs.map(t => (
                              <span key={String(t)} style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'rgba(200,168,75,0.1)', color: C.gold }}>
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {r.seller_public_reply?.trim() ? (
                          <div
                            style={{
                              marginTop: 10,
                              padding: '8px 10px',
                              borderRadius: 8,
                              background: 'rgba(200,168,75,0.08)',
                              border: '0.5px solid rgba(200,168,75,0.22)',
                            }}
                          >
                            <div style={{ fontSize: 9, color: C.gold, fontWeight: 700, marginBottom: 4 }}>Tu respuesta pública</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)' }}>{r.seller_public_reply.trim()}</div>
                          </div>
                        ) : null}
                      </DCard>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {tab === 'dadas' && (
          <div style={{ padding: '20px' }}>
            {!user?.id ? (
              <DCard style={{ padding: 16 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Inicia sesión para ver las valoraciones que has enviado.</div>
              </DCard>
            ) : givenLoading ? (
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando…</div>
            ) : (
              <>
                <DCard style={{ display: 'flex', gap: 16, padding: 16, marginBottom: 14, alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: 34, fontWeight: 500, color: C.gold, lineHeight: 1 }}>
                      {givenStats.count === 0 ? '—' : givenStats.avg}
                    </div>
                    <SurcosRating value={givenStats.surcos} readOnly size="md" count={givenStats.count} />
                  </div>
                  <div style={{ flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
                    Media de las valoraciones <strong style={{ color: 'rgba(255,255,255,0.75)' }}>visibles</strong> que has enviado tras tus compras y ventas.
                  </div>
                </DCard>

                {!given.length ? (
                  <DCard style={{ padding: 16 }}>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Aún no has enviado ninguna valoración.</div>
                  </DCard>
                ) : (
                  given.map(r => {
                    const rev = reviewedRow(r);
                    const uname = rev.username ?? 'Usuario';
                    const sur = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 1)));
                    const tgs = Array.isArray(r.tags) ? r.tags : [];
                    return (
                      <DCard key={r.id} style={{ padding: 14, marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <Avatar initials={uname.slice(0, 2).toUpperCase()} bg="#534AB7" color="#EEEDFE" size={32} imageUrl={rev.avatar_url ?? null} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>@{uname}</div>
                            <SurcosRating value={sur} readOnly size="sm" />
                          </div>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : ''}
                          </span>
                        </div>
                        {!r.is_visible ? (
                          <div style={{ fontSize: 10, color: 'rgba(239,159,39,0.95)', marginBottom: 6 }}>Pendiente de revelación mutua</div>
                        ) : null}
                        {r.comment?.trim() ? (
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 8 }}>{r.comment.trim()}</div>
                        ) : null}
                        {tgs.length ? (
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {tgs.map(t => (
                              <span key={String(t)} style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'rgba(200,168,75,0.1)', color: C.gold }}>
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </DCard>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {tab === 'pendientes' && (
          <div style={{ padding: '20px' }}>
            {!user?.id ? (
              <DCard style={{ padding: 16 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Inicia sesión para ver valoraciones pendientes.</div>
              </DCard>
            ) : pendingLoading ? (
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando…</div>
            ) : pendingCount === 0 ? (
              <>
                <DCard style={{ padding: 16, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{pendingCountLabel}</div>
                </DCard>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '20px 0', lineHeight: 1.6 }}>
                  Cuando completes una compra o venta podrás valorar aquí a la otra parte.
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: C.gold, marginBottom: 12, fontWeight: 500 }}>{pendingCountLabel}</div>
                {pending.map(item => {
                  const listing = item.listing ?? null;
                  const cover = listingCoverUrl(listing);
                  const cp = item.counterparty ?? {};
                  const uname = cp.username ?? 'usuario';
                  return (
                    <DCard
                      key={item.transactionId}
                      style={{
                        padding: '12px 14px',
                        marginBottom: 10,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                      onClick={() => openPendingItem(item)}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 8,
                          background: '#2d1b69',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {cover ? (
                          <img src={cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Icon name="disc" size={16} color="rgba(255,255,255,0.3)" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>
                          {listing?.title || listing?.artist || 'Transacción'}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                          @{uname} · {counterpartyRoleLabel(item.role)}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: C.gold }}>→</span>
                    </DCard>
                  );
                })}
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '20px 0', lineHeight: 1.6 }}>
                  Las valoraciones pendientes caducan a los 30 días.
                  <br />
                  Tu opinión ayuda a construir una comunidad de confianza.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
